import { MUSIC, PHYSICS, SESSION_LENGTH_S } from '../utils/constants';

// CRANKED UP FOR HIGH ADRENALINE!
const BGM_VOLUME = 0.6;
const ENGINE_VOLUME_MULT = 2.0;
const SIREN_VOLUME_MULT = 4.5; // VERY LOUD SIREN!
const HELICOPTER_VOLUME = 1.0; // MAX HELICOPTER VOLUME!

const AUDIO_URLS = {
  engineIdle: '/audio/engine-idle.mp3',
  engineMid: '/audio/engine-mid.mp3',
  engineHigh: '/audio/engine-high.mp3',
  tireSqueal: '/audio/tire-squeal.mp3',
  siren: '/audio/siren.mp3',
  crash: '/audio/crash.mp3',
  rain: '/audio/rain-ambient.mp3',
  helicopter: '/audio/soundreality-helicopter-129052.mp3', // User's choice - LOUD!
} as const;

/**
 * Optional short MP3 one-shots (paths under `public/`). When listed, some bursts play a real
 * clip with procedural megaphone underneath; when empty, megaphone + synthetic radio only.
 * Example: `'/audio/dispatch-01.mp3'`
 */
export const RADIO_CHATTER_CLIP_URLS: string[] = [];

/**
 * prd2 Week 2 Day 12: engine layers, collision, drift screech, cop siren (spatial-ish).
 * prd2 Week 2 Day 13: dynamic music layers — tension from timer + chase + last 30/10s.
 * Web Audio — no external assets.
 */
export class GameAudio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private engineLow: OscillatorNode | null = null;
  private engineMid: OscillatorNode | null = null;
  private engineGain: GainNode | null = null;
  private engineMidGain: GainNode | null = null;
  private engineLowLP: BiquadFilterNode | null = null;
  private engineMidLP: BiquadFilterNode | null = null;
  /** Muffles engine + mid when driving under highway decks. */
  private engineTunnelLP: BiquadFilterNode | null = null;
  private tunnelConvolver: ConvolverNode | null = null;
  private tunnelReverbWet: GainNode | null = null;
  private driftNoise: AudioBufferSourceNode | null = null;
  private driftGain: GainNode | null = null;
  private driftBuf: AudioBuffer | null = null;
  private sirenA: OscillatorNode | null = null;
  private sirenB: OscillatorNode | null = null;
  private sirenGain: GainNode | null = null;
  private sirenPanner: StereoPannerNode | null = null;
  private sirenPhase = 0;
  /** Real siren audio element (MP3 file). */
  private sirenEl: HTMLAudioElement | null = null;
  private usingSirenFile = false;
  private prevCopDist = 400;
  private collisionGain: GainNode | null = null;
  private collisionNoise: AudioBufferSourceNode | null = null;
  private windGain: GainNode | null = null;
  private windNoise: AudioBufferSourceNode | null = null;
  private windFilter: BiquadFilterNode | null = null;
  private nitroGain: GainNode | null = null;
  private nitroOsc: OscillatorNode | null = null;
  private started = false;
  private collisionPunch = 0;

  /** Music bus (parallel to SFX `master`). */
  private musicBus: GainNode | null = null;
  private musicPadGain: GainNode | null = null;
  private musicPercGain: GainNode | null = null;
  private musicPadA: OscillatorNode | null = null;
  private musicPadB: OscillatorNode | null = null;
  private musicNoiseSrc: AudioBufferSourceNode | null = null;
  private musicLP: BiquadFilterNode | null = null;
  private musicPercBP: BiquadFilterNode | null = null;
  private musicPulsePhase = 0;
  private musicPhasePrev: 'playing' | 'won' | 'lost' = 'playing';
  /** High-adrenaline beat sequencer state */
  private beatStep = 0;
  private beatTimer = 0;
  private beatBpm = 140;
  private chaseIntensity = 0;
  /** 0→1 after first `ensureStarted` — eases in SFX + music so idle isn’t a wall of noise. */
  private bootFade = 0;
  /** Stress heartbeat phase accumulator (lub–dub rate scales with heat). */
  private heartPulseAccum = 0;
  /** Optional BGM file — procedural bed ducks when this plays. */
  private bgmEl: HTMLAudioElement | null = null;
  /** Rain ambient loop for night atmosphere. */
  private rainEl: HTMLAudioElement | null = null;
  /** Helicopter rotor loop for heat level 5. */
  private helicopterEl: HTMLAudioElement | null = null;
  private helicopterActive = false;

  /** 0–1 — brief music duck on huge impacts (lets SFX punch through). */
  private musicImpactDuck = 0;

  /** After round restart — avoids odd transitions when starting a new run after win/lose. */
  resetMusicPhase(): void {
    this.musicPhasePrev = 'playing';
    this.musicPulsePhase = 0;
    this.musicImpactDuck = 0;
    this.heartPulseAccum = 0;
  }

  /** Call on large collisions — ducks procedural music briefly (high-impact moments). */
  bumpMusicImpactDuck(intensity01: number): void {
    this.musicImpactDuck = Math.min(
      1,
      this.musicImpactDuck + Math.max(0, intensity01) * 0.95,
    );
  }

  /** Synthetic IR — tunnel “concrete” tail without loading assets. */
  private makeTunnelImpulse(ctx: AudioContext): AudioBuffer {
    const dur = 0.52;
    const len = Math.floor(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch);
      for (let i = 0; i < len; i++) {
        const t = i / ctx.sampleRate;
        const decay = Math.exp(-t * 5.2);
        d[i] = (Math.random() * 2 - 1) * decay * 0.48;
      }
    }
    return buf;
  }

  /** Call from first pointer / key so AudioContext can resume. */
  ensureStarted(): void {
    if (typeof window === 'undefined' || typeof AudioContext === 'undefined') {
      return;
    }
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const ctx = new AudioContext();
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.master.gain.value = 0.85; // CRANKED UP!
    this.master.connect(ctx.destination);

    this.engineGain = ctx.createGain();
    this.engineGain.gain.value = 0;
    this.engineLow = ctx.createOscillator();
    this.engineLow.type = 'sine';
    this.engineLow.frequency.value = 58;
    this.engineLowLP = ctx.createBiquadFilter();
    this.engineLowLP.type = 'lowpass';
    this.engineLowLP.frequency.value = 340;
    this.engineLowLP.Q.value = 0.65;
    this.engineLow.connect(this.engineLowLP);
    this.engineLowLP.connect(this.engineGain);

    this.engineMidGain = ctx.createGain();
    this.engineMidGain.gain.value = 0;
    this.engineMid = ctx.createOscillator();
    this.engineMid.type = 'triangle';
    this.engineMid.frequency.value = 118;
    this.engineMidLP = ctx.createBiquadFilter();
    this.engineMidLP.type = 'lowpass';
    this.engineMidLP.frequency.value = 820;
    this.engineMidLP.Q.value = 0.75;
    this.engineMid.connect(this.engineMidLP);
    this.engineMidLP.connect(this.engineMidGain);

    this.engineTunnelLP = ctx.createBiquadFilter();
    this.engineTunnelLP.type = 'lowpass';
    this.engineTunnelLP.frequency.value = 22000;
    this.engineTunnelLP.Q.value = 0.65;
    this.engineGain.connect(this.engineTunnelLP);
    this.engineMidGain.connect(this.engineTunnelLP);
    this.engineTunnelLP.connect(this.master);

    this.tunnelConvolver = ctx.createConvolver();
    this.tunnelConvolver.buffer = this.makeTunnelImpulse(ctx);
    this.tunnelReverbWet = ctx.createGain();
    this.tunnelReverbWet.gain.value = 0;
    this.engineTunnelLP.connect(this.tunnelConvolver);
    this.tunnelConvolver.connect(this.tunnelReverbWet);
    this.tunnelReverbWet.connect(this.master);

    this.engineLow.start();
    this.engineMid.start();

    this.collisionGain = ctx.createGain();
    this.collisionGain.gain.value = 0;
    const colLoop = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.04), ctx.sampleRate);
    const cl = colLoop.getChannelData(0);
    for (let i = 0; i < cl.length; i++) {
      cl[i] = Math.random() * 2 - 1;
    }
    const colSrc = ctx.createBufferSource();
    colSrc.buffer = colLoop;
    colSrc.loop = true;
    colSrc.connect(this.collisionGain);
    colSrc.start();
    this.collisionNoise = colSrc;
    this.collisionGain.connect(this.master);

    this.windGain = ctx.createGain();
    this.windGain.gain.value = 0;
    this.windFilter = ctx.createBiquadFilter();
    this.windFilter.type = 'bandpass';
    this.windFilter.frequency.value = 900;
    this.windFilter.Q.value = 0.85;
    const windBuf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.28), ctx.sampleRate);
    const windData = windBuf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < windData.length; i++) {
      last = last * 0.82 + (Math.random() * 2 - 1) * 0.18;
      windData[i] = last;
    }
    const windSrc = ctx.createBufferSource();
    windSrc.buffer = windBuf;
    windSrc.loop = true;
    windSrc.connect(this.windFilter);
    this.windFilter.connect(this.windGain);
    this.windGain.connect(this.master);
    windSrc.start();
    this.windNoise = windSrc;

    this.nitroGain = ctx.createGain();
    this.nitroGain.gain.value = 0;
    this.nitroOsc = ctx.createOscillator();
    this.nitroOsc.type = 'triangle';
    this.nitroOsc.frequency.value = 180;
    const nitroFilt = ctx.createBiquadFilter();
    nitroFilt.type = 'bandpass';
    nitroFilt.frequency.value = 420;
    this.nitroOsc.connect(nitroFilt);
    nitroFilt.connect(this.nitroGain);
    this.nitroGain.connect(this.master);
    this.nitroOsc.start();

    this.sirenGain = ctx.createGain();
    this.sirenGain.gain.value = 0;
    this.sirenA = ctx.createOscillator();
    this.sirenB = ctx.createOscillator();
    this.sirenA.type = 'sine';
    this.sirenB.type = 'sine';
    this.sirenA.frequency.value = 760;
    this.sirenB.frequency.value = 960;
    this.sirenA.connect(this.sirenGain);
    this.sirenB.connect(this.sirenGain);
    if (typeof ctx.createStereoPanner === 'function') {
      this.sirenPanner = ctx.createStereoPanner();
      this.sirenGain.connect(this.sirenPanner);
      this.sirenPanner.connect(this.master);
    } else {
      this.sirenGain.connect(this.master);
    }
    this.sirenA.start();
    this.sirenB.start();
    
    // Try to load real siren MP3 file
    this.initSirenFile(ctx);

    const driftLen = Math.floor(ctx.sampleRate * 0.5);
    this.driftBuf = ctx.createBuffer(1, driftLen, ctx.sampleRate);
    const dd = this.driftBuf.getChannelData(0);
    let b = 0;
    for (let i = 0; i < driftLen; i++) {
      const w = (Math.random() * 2 - 1) * 0.18;
      b = b * 0.96 + w;
      dd[i] = b;
    }

    this.driftGain = ctx.createGain();
    this.driftGain.gain.value = 0;
    const driftFilt = ctx.createBiquadFilter();
    driftFilt.type = 'bandpass';
    driftFilt.frequency.value = 1800;
    driftFilt.Q.value = 0.9;
    const driftSrc = ctx.createBufferSource();
    driftSrc.buffer = this.driftBuf;
    driftSrc.loop = true;
    driftSrc.connect(driftFilt);
    driftFilt.connect(this.driftGain);
    this.driftGain.connect(this.master);
    driftSrc.start();
    this.driftNoise = driftSrc;

    this.initMusic(ctx);

    void this.loadOptionalRadioClips(ctx);

    this.started = true;
    this.bootFade = 0;
    this.tryStartBgm();
    this.tryStartRainAmbient();
    this.initHelicopterAudio(ctx);
  }

  private initHelicopterAudio(_ctx: AudioContext): void {
    if (typeof window === 'undefined' || typeof Audio === 'undefined') return;
    if (this.helicopterEl) return; // Already initialized
    
    // Use direct HTML audio - simpler and more reliable
    const a = new Audio(AUDIO_URLS.helicopter);
    a.loop = true;
    a.preload = 'auto';
    a.volume = HELICOPTER_VOLUME;
    
    a.addEventListener(
      'error',
      () => {
        /* missing asset — rotor stays silent */
      },
      { once: true },
    );
    
    this.helicopterEl = a;
    
    // Pre-load the audio
    a.load();
  }

  updateHelicopter(active: boolean, _distance: number = 100): void {
    if (!this.helicopterEl) {
      // Try to reinitialize if missing
      if (this.ctx) {
        this.initHelicopterAudio(this.ctx);
      }
      return;
    }
    
    if (active && !this.helicopterActive) {
      this.helicopterActive = true;
      this.helicopterEl.currentTime = 0;
      this.helicopterEl.volume = HELICOPTER_VOLUME;
      void this.helicopterEl.play().catch((e) => {
        void e;
        // Try to reload and play again
        this.helicopterEl!.load();
        setTimeout(() => {
          if (this.helicopterEl) {
            this.helicopterEl.volume = HELICOPTER_VOLUME;
            void this.helicopterEl.play().catch(() => {});
          }
        }, 100);
      });
    } else if (!active && this.helicopterActive) {
      this.helicopterActive = false;
      this.helicopterEl.pause();
      this.helicopterEl.volume = 0;
    }

    if (active && this.helicopterEl) {
      // ALWAYS FULL VOLUME when active - helicopter is close!
      this.helicopterEl.volume = HELICOPTER_VOLUME;

      // Ensure it's playing
      if (this.helicopterEl.paused) {
        void this.helicopterEl.play().catch(() => {});
      }
    }
  }

  private initSirenFile(_ctx: AudioContext): void {
    if (typeof window === 'undefined' || typeof Audio === 'undefined') return;
    
    // Use direct HTML audio - simpler and more reliable
    const a = new Audio(AUDIO_URLS.siren);
    a.loop = true;
    a.preload = 'auto';
    a.volume = 0;
    
    a.addEventListener('canplaythrough', () => {
      this.usingSirenFile = true;
    }, { once: true });
    
    a.addEventListener('error', () => {
      this.usingSirenFile = false;
    }, { once: true });
    
    this.sirenEl = a;
    a.load();
  }

  private tryStartRainAmbient(): void {
    if (typeof window === 'undefined' || typeof Audio === 'undefined') return;
    if (this.rainEl) return;
    const a = new Audio(AUDIO_URLS.rain);
    a.loop = true;
    a.preload = 'auto';
    a.volume = 0.15;
    a.addEventListener('error', () => {
      this.rainEl = null;
    });
    this.rainEl = a;
    void a.play().catch(() => {});
  }

  private tryStartBgm(): void {
    if (typeof window === 'undefined' || typeof Audio === 'undefined') return;
    if (this.bgmEl) return;
    const a = new Audio(MUSIC.BGM_URL);
    a.loop = true;
    a.preload = 'auto';
    a.volume = BGM_VOLUME;
    a.addEventListener('error', () => {
      this.bgmEl = null;
    });
    this.bgmEl = a;
    a.playbackRate = MUSIC.BGM_BASE_PLAYBACK_RATE;
    void a.play().catch(() => {
      /* missing file or autoplay policy — keep procedural music */
    });
  }

  /** Call after user gesture if the first `play()` was blocked. */
  retryBgmAfterUnlock(): void {
    if (!this.bgmEl) return;
    void this.bgmEl.play().catch(() => {});
    if (this.rainEl) {
      void this.rainEl.play().catch(() => {});
    }
  }

  private initMusic(ctx: AudioContext): void {
    this.musicBus = ctx.createGain();
    this.musicBus.gain.value = MUSIC.MASTER;

    this.musicPadGain = ctx.createGain();
    this.musicPadGain.gain.value = 0.06;

    this.musicLP = ctx.createBiquadFilter();
    this.musicLP.type = 'lowpass';
    this.musicLP.frequency.value = 520;
    this.musicLP.Q.value = 0.7;

    this.musicPadA = ctx.createOscillator();
    this.musicPadA.type = 'sine';
    this.musicPadA.frequency.value = 65.41;
    this.musicPadB = ctx.createOscillator();
    this.musicPadB.type = 'triangle';
    this.musicPadB.frequency.value = 98.2;

    this.musicPadA.connect(this.musicPadGain);
    this.musicPadB.connect(this.musicPadGain);
    this.musicPadGain.connect(this.musicLP);

    const noiseBuf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.4), ctx.sampleRate);
    const nd = noiseBuf.getChannelData(0);
    for (let i = 0; i < nd.length; i++) {
      nd[i] = (Math.random() * 2 - 1) * 0.35;
    }
    this.musicNoiseSrc = ctx.createBufferSource();
    this.musicNoiseSrc.buffer = noiseBuf;
    this.musicNoiseSrc.loop = true;

    this.musicPercBP = ctx.createBiquadFilter();
    this.musicPercBP.type = 'bandpass';
    this.musicPercBP.frequency.value = 3200;
    this.musicPercBP.Q.value = 1.1;

    this.musicPercGain = ctx.createGain();
    this.musicPercGain.gain.value = 0;

    this.musicNoiseSrc.connect(this.musicPercBP);
    this.musicPercBP.connect(this.musicPercGain);

    this.musicLP.connect(this.musicBus);
    this.musicPercGain.connect(this.musicBus);
    this.musicBus.connect(ctx.destination);

    this.musicPadA.start();
    this.musicPadB.start();
    this.musicNoiseSrc.start();
  }

  dispose(): void {
    try {
      this.engineLow?.stop();
      this.engineMid?.stop();
      this.sirenA?.stop();
      this.sirenB?.stop();
      this.driftNoise?.stop();
      this.collisionNoise?.stop();
      this.windNoise?.stop();
      this.nitroOsc?.stop();
      this.musicPadA?.stop();
      this.musicPadB?.stop();
      this.musicNoiseSrc?.stop();
    } catch {
      /* ignore */
    }
    if (this.bgmEl) {
      this.bgmEl.pause();
      this.bgmEl.src = '';
      this.bgmEl = null;
    }
    if (this.rainEl) {
      this.rainEl.pause();
      this.rainEl.src = '';
      this.rainEl = null;
    }
    this.ctx?.close();
    this.ctx = null;
  }

  triggerCollision(intensity: number): void {
    this.collisionPunch = Math.min(1, this.collisionPunch + intensity * 0.85);
  }

  /** Premium run start cue: short scanner sweep + engine thump, fully procedural. */
  playRunStartCue(): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    void ctx.resume();
    const t = ctx.currentTime;
    this.musicImpactDuck = Math.min(1, this.musicImpactDuck + 0.38);

    const sweep = ctx.createOscillator();
    sweep.type = 'sawtooth';
    sweep.frequency.setValueAtTime(110, t);
    sweep.frequency.exponentialRampToValueAtTime(880, t + 0.36);
    const sweepFilter = ctx.createBiquadFilter();
    sweepFilter.type = 'bandpass';
    sweepFilter.frequency.setValueAtTime(640, t);
    sweepFilter.frequency.exponentialRampToValueAtTime(2600, t + 0.36);
    sweepFilter.Q.value = 8;
    const sweepGain = ctx.createGain();
    sweepGain.gain.setValueAtTime(0.0001, t);
    sweepGain.gain.exponentialRampToValueAtTime(0.12, t + 0.035);
    sweepGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.46);
    sweep.connect(sweepFilter);
    sweepFilter.connect(sweepGain);
    sweepGain.connect(this.master);
    sweep.start(t);
    sweep.stop(t + 0.48);

    const thump = ctx.createOscillator();
    thump.type = 'sine';
    thump.frequency.setValueAtTime(82, t + 0.28);
    thump.frequency.exponentialRampToValueAtTime(38, t + 0.7);
    const thumpGain = ctx.createGain();
    thumpGain.gain.setValueAtTime(0.0001, t + 0.28);
    thumpGain.gain.exponentialRampToValueAtTime(0.22, t + 0.31);
    thumpGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.82);
    thump.connect(thumpGain);
    thumpGain.connect(this.master);
    thump.start(t + 0.28);
    thump.stop(t + 0.84);

    const click = ctx.createOscillator();
    click.type = 'square';
    click.frequency.setValueAtTime(1850, t + 0.08);
    const clickGain = ctx.createGain();
    clickGain.gain.setValueAtTime(0.0001, t + 0.08);
    clickGain.gain.exponentialRampToValueAtTime(0.06, t + 0.09);
    clickGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.145);
    click.connect(clickGain);
    clickGain.connect(this.master);
    click.start(t + 0.08);
    click.stop(t + 0.16);
  }

  /** Pursuit breaker explosion: bass thud + brief music silence. */
  triggerExplosionBoom(): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    void ctx.resume();
    const t = ctx.currentTime;
    // Sub-bass thud
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(55, t);
    osc.frequency.exponentialRampToValueAtTime(22, t + 0.4);
    gain.gain.setValueAtTime(0.9, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
    osc.connect(gain);
    gain.connect(this.master);
    osc.start(t);
    osc.stop(t + 0.55);
    // Noise crunch layer
    const noiseLen = Math.floor(ctx.sampleRate * 0.3);
    const noiseBuf = ctx.createBuffer(1, noiseLen, ctx.sampleRate);
    const noiseArr = noiseBuf.getChannelData(0);
    for (let i = 0; i < noiseLen; i++) noiseArr[i] = (Math.random() * 2 - 1) * 0.7;
    const nSrc = ctx.createBufferSource();
    nSrc.buffer = noiseBuf;
    const nGain = ctx.createGain();
    nGain.gain.setValueAtTime(0.5, t);
    nGain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
    const nFilter = ctx.createBiquadFilter();
    nFilter.type = 'lowpass';
    nFilter.frequency.setValueAtTime(800, t);
    nFilter.frequency.exponentialRampToValueAtTime(100, t + 0.3);
    nSrc.connect(nFilter);
    nFilter.connect(nGain);
    nGain.connect(this.master);
    nSrc.start(t);
    nSrc.stop(t + 0.35);
    // Full duck on music for 0.2s then recover
    this.musicImpactDuck = 1.0;
  }

  update(
    dt: number,
    opts: {
      speedMs: number;
      throttle: number;
      handbrake: boolean;
      nitroBoost: boolean;
      drift: boolean;
      copDist: number;
      bustMeter: number;
      muted: boolean;
      /** -1 = left, 1 = right — nearest cop relative to camera. */
      sirenPan?: number;
      /** Under elevated highway — muffled engine. */
      inTunnel?: boolean;
      /** Heat level 1–5 — faster / louder stress pulse when higher. */
      wantedStars?: number;
      /** Silence adrenaline bed (e.g. during victory celebration payoff). */
      suppressAdrenalinePulse?: boolean;
    },
  ): void {
    if (!this.ctx || !this.started || !this.master) return;
    const t = this.ctx.currentTime;

    this.bootFade = Math.min(1, this.bootFade + dt * 0.85);
    const bootEase = this.bootFade * this.bootFade;
    const sfxMul = bootEase;

    this.master.gain.setTargetAtTime(opts.muted ? 0 : 0.48 * sfxMul, t, 0.05);

    const maxS = PHYSICS.NITRO_MAX_SPEED_MS;
    const ratio = maxS > 1e-6 ? Math.min(1, opts.speedMs / maxS) : 0;
    const idleGate =
      0.12 +
      0.88 *
        Math.min(
          1,
          ratio * 1.15 +
            Math.abs(opts.throttle) * 0.55 +
            (opts.handbrake ? 0.12 : 0),
        );
    if (this.engineLow) {
      this.engineLow.frequency.setTargetAtTime(52 + ratio * 88, t, 0.04);
    }
    if (this.engineMid) {
      this.engineMid.frequency.setTargetAtTime(105 + ratio * 200, t, 0.04);
    }
    const lpOpen = 380 + ratio * 2200;
    const lpMidOpen = 900 + ratio * 2800;
    this.engineLowLP?.frequency.setTargetAtTime(lpOpen, t, 0.06);
    this.engineMidLP?.frequency.setTargetAtTime(lpMidOpen, t, 0.06);

    const tunnelCut = opts.inTunnel ? 2600 : 22000;
    this.engineTunnelLP?.frequency.setTargetAtTime(tunnelCut, t, 0.1);
    this.tunnelReverbWet?.gain.setTargetAtTime(
      opts.inTunnel ? 0.14 : 0,
      t,
      0.14,
    );

    // CRANKED UP ENGINE SOUND!
    const baseVol = (0.08 + ratio * 0.55) * idleGate * ENGINE_VOLUME_MULT;
    const throttleMix = 0.35 + Math.abs(opts.throttle) * 0.65;
    this.engineGain?.gain.setTargetAtTime(baseVol * throttleMix, t, 0.04);
    const midVol =
      ratio > 0.3 ? (ratio - 0.3) * 1.6 * throttleMix * idleGate : 0;
    this.engineMidGain?.gain.setTargetAtTime(midVol * 0.7 * ENGINE_VOLUME_MULT, t, 0.05);

    if (opts.nitroBoost) {
      this.nitroGain?.gain.setTargetAtTime(0.45 * sfxMul, t, 0.02); // LOUDER NITRO!
    } else {
      this.nitroGain?.gain.setTargetAtTime(0, t, 0.08);
    }

    this.collisionPunch = Math.max(0, this.collisionPunch - dt * 4.2);
    this.collisionGain?.gain.setTargetAtTime(this.collisionPunch * 0.55 * sfxMul, t, 0.02);

    const driftAmt =
      opts.drift && opts.speedMs > 4 ? 0.12 + Math.min(0.32, opts.speedMs * 0.012) : 0;
    this.driftGain?.gain.setTargetAtTime(driftAmt * sfxMul, t, 0.05);

    const windT = Math.max(0, Math.min(1, (ratio - 0.34) / 0.66));
    const windVol = opts.muted ? 0 : windT * windT * (opts.nitroBoost ? 0.16 : 0.095) * sfxMul;
    this.windGain?.gain.setTargetAtTime(windVol, t, 0.12);
    this.windFilter?.frequency.setTargetAtTime(680 + windT * 2400, t, 0.16);

    const rawClosing =
      (this.prevCopDist - opts.copDist) / Math.max(dt, 1e-4);
    const closing = Math.max(-95, Math.min(95, rawClosing));
    const dop = Math.max(-0.065, Math.min(0.065, closing / 145));

    this.sirenPhase += dt * 4.8;
    const alt = (Math.sin(this.sirenPhase) + 1) * 0.5;
    const chaseAudible = Math.min(
      1,
      opts.bustMeter * 2.8 + opts.speedMs / 35 + 1 / (1 + opts.copDist / 180),
    );
    const sirenVol =
      ((0.008 + opts.bustMeter * 0.38) / (1 + opts.copDist / 220) +
        0.035 / (1 + opts.copDist / 400)) *
      chaseAudible;
    
    // LOUD POLICE SIREN!
    const targetSirenGain = Math.min(0.85, sirenVol * sfxMul * SIREN_VOLUME_MULT * (0.5 + alt * 0.5));

    if (this.sirenPanner) {
      const pan = Math.max(-1, Math.min(1, opts.sirenPan ?? 0));
      this.sirenPanner.pan.setTargetAtTime(pan, t, 0.05);
    }

    // Use real siren file if available, otherwise fall back to oscillators
    if (this.usingSirenFile && this.sirenEl) {
      // Mute oscillator siren
      this.sirenGain?.gain.setTargetAtTime(0, t, 0.05);
      
      // Control file-based siren with direct volume - VERY LOUD!
      const targetVol = Math.min(1.0, targetSirenGain * 2.0); // Big boost for file
      this.sirenEl.volume += (targetVol - this.sirenEl.volume) * 0.2; // Faster response
      
      // Start/stop the audio element based on volume
      if (targetVol > 0.01 && this.sirenEl.paused) {
        void this.sirenEl.play().catch(() => {});
      } else if (targetVol < 0.005 && !this.sirenEl.paused) {
        this.sirenEl.pause();
      }
    } else {
      // Use oscillator-based siren
      this.sirenGain?.gain.setTargetAtTime(targetSirenGain, t, 0.08);
      
      if (this.sirenA && this.sirenB) {
        const wobble = Math.sin(this.sirenPhase * 1.7) * 12;
        this.sirenA.frequency.setTargetAtTime(
          (760 + alt * 40 + wobble) * (1 + dop),
          t,
          0.02,
        );
        this.sirenB.frequency.setTargetAtTime(
          (960 - alt * 50 + wobble * 0.5) * (1 + dop),
          t,
          0.02,
        );
      }
    }

    this.tickStressHeartbeat(dt, opts, sfxMul);

    this.prevCopDist = opts.copDist;
  }

  /**
   * Procedural lub–dub: sub thumps through `master`, rate & mix scale with heat + bust + cop proximity.
   */
  private tickStressHeartbeat(
    dt: number,
    opts: {
      copDist: number;
      bustMeter: number;
      muted: boolean;
      wantedStars?: number;
      suppressAdrenalinePulse?: boolean;
    },
    sfxMul: number,
  ): void {
    if (!this.ctx || !this.master) return;
    if (opts.muted || opts.suppressAdrenalinePulse) {
      this.heartPulseAccum = 0;
      return;
    }
    const wsRaw = opts.wantedStars;
    if (wsRaw === undefined) {
      this.heartPulseAccum = 0;
      return;
    }
    const wsRounded = Math.round(wsRaw);
    if (wsRounded < 1) {
      this.heartPulseAccum = 0;
      return;
    }
    const ws = Math.min(5, wsRounded);
    const proximity = 1 / (1 + opts.copDist / 200);
    const stress = Math.min(
      1,
      opts.bustMeter * 0.92 + proximity * 0.38 + (ws - 1) * 0.04,
    );
    const bpm = 54 + ws * 19 + stress * 26;
    const bps = bpm / 60;
    this.heartPulseAccum += dt * bps;
    let guard = 0;
    while (this.heartPulseAccum >= 1 && guard < 3) {
      this.heartPulseAccum -= 1;
      guard++;
      this.playStressHeartbeatPair(ws, stress, sfxMul, proximity);
    }
    if (this.heartPulseAccum > 2) this.heartPulseAccum %= 1;
  }

  private playStressHeartbeatPair(
    ws: number,
    stress: number,
    sfxMul: number,
    proximity: number,
  ): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    void ctx.resume();
    const t = ctx.currentTime;
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const heatMix = 0.34 + ws * 0.11;
    let vol =
      0.034 *
      heatMix *
      (0.38 + stress * 0.62) *
      (0.45 + proximity * 0.55) *
      sfxMul;
    if (reduce) vol *= 0.32;
    vol = Math.min(0.1, vol);

    const split = 0.088 - (ws - 1) * 0.005;
    this.playHeartThump(ctx, t, 64 + ws * 2.2, vol, 0.008, 0.074);
    this.playHeartThump(
      ctx,
      t + split,
      54 + ws * 1.6,
      vol * (0.44 + stress * 0.12),
      0.007,
      0.058,
    );
  }

  private playHeartThump(
    ctx: AudioContext,
    start: number,
    hz: number,
    peak: number,
    attack: number,
    decay: number,
  ): void {
    if (!this.master || peak < 0.0002) return;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 520;
    lp.Q.value = 0.7;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, start);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), start + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, start + attack + decay);
    osc.frequency.setValueAtTime(hz, start);
    osc.connect(lp);
    lp.connect(g);
    g.connect(this.master);
    osc.start(start);
    osc.stop(start + attack + decay + 0.04);
  }

  /**
   * prd2 Day 13 — ambient pad + noise “drum” pulse; filter opens as timer runs down;
   * last 30s / 10s add energy; win/lose one-shots.
   */
  updateMusic(
    dt: number,
    opts: {
      phase: 'playing' | 'won' | 'lost';
      sessionRemainingS: number;
      bustMeter: number;
      wantedStars: number;
    },
  ): void {
    if (!this.ctx || !this.started || !this.musicBus || !this.musicPadGain) return;
    const t = this.ctx.currentTime;

    this.musicImpactDuck = Math.max(0, this.musicImpactDuck - dt * 2.85);

    if (opts.phase !== this.musicPhasePrev) {
      if (opts.phase === 'won') this.playWinStinger(t);
      else if (opts.phase === 'lost') this.playLoseStinger(t);
      this.musicPhasePrev = opts.phase;
    }

    if (opts.phase !== 'playing') {
      this.musicBus.gain.setTargetAtTime(0.02, t, 0.12);
      return;
    }

    if (this.bgmEl) {
      this.bgmEl.playbackRate = MUSIC.BGM_BASE_PLAYBACK_RATE;
    }

    if (this.bgmEl && !this.bgmEl.paused) {
      this.musicPadGain.gain.setTargetAtTime(0, t, 0.1);
      this.musicPercGain?.gain.setTargetAtTime(0, t, 0.08);
      this.musicBus.gain.setTargetAtTime(0, t, 0.12);
      return;
    }

    const bootEase = this.bootFade * this.bootFade;

    const timeT = 1 - opts.sessionRemainingS / SESSION_LENGTH_S;
    const chaseT = opts.bustMeter * 0.52 + (opts.wantedStars / 5) * 0.28;
    const last30 = opts.sessionRemainingS < MUSIC.LAST_30_S ? 0.2 : 0;
    const last10 = opts.sessionRemainingS < MUSIC.LAST_10_S ? 0.32 : 0;
    const tension = Math.min(1, timeT * 0.48 + chaseT * 0.42 + last30 + last10);

    this.musicPulsePhase += dt * (2.8 + tension * 7.5) * Math.PI * 2;
    const pulse =
      0.52 + 0.48 * (0.5 + 0.5 * Math.sin(this.musicPulsePhase));

    this.musicPadGain.gain.setTargetAtTime(0.038 + tension * 0.17, t, 0.08);
    this.musicPercGain?.gain.setTargetAtTime(
      (0.006 + tension * 0.14 + last10 * 0.1) * pulse * (0.45 + bootEase * 0.55),
      t,
      0.06,
    );
    this.musicLP?.frequency.setTargetAtTime(
      380 + tension * 2200 + last30 * 420 + last10 * 800,
      t,
      0.06,
    );
    this.musicPercBP?.frequency.setTargetAtTime(2400 + tension * 2000, t, 0.08);

    const duck = 1 - this.musicImpactDuck * 0.72;
    this.musicBus.gain.setTargetAtTime(
      MUSIC.MASTER * (0.65 + tension * 0.5) * bootEase * duck,
      t,
      0.05,
    );

    if (this.musicPadA && this.musicPadB) {
      const det = Math.sin(this.musicPulsePhase * 0.5) * 1.2;
      this.musicPadA.frequency.setTargetAtTime(65.41 + tension * 18 + det, t, 0.05);
      this.musicPadB.frequency.setTargetAtTime(98.2 + tension * 28 - det * 0.5, t, 0.05);
    }

    if (tension > 0.4) {
      this.updateChaseBeat(dt, tension);
    }
  }

  private playWinStinger(start: number): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(196, start);
    o.frequency.exponentialRampToValueAtTime(784, start + 0.28);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, start);
    g.gain.exponentialRampToValueAtTime(0.2, start + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, start + 0.55);
    o.connect(g);
    g.connect(ctx.destination);
    o.start(start);
    o.stop(start + 0.56);
  }

  private playLoseStinger(start: number): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const o = ctx.createOscillator();
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(280, start);
    o.frequency.exponentialRampToValueAtTime(48, start + 0.55);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, start);
    g.gain.exponentialRampToValueAtTime(0.14, start + 0.04);
    g.gain.exponentialRampToValueAtTime(0.0001, start + 0.65);
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 900;
    o.connect(f);
    f.connect(g);
    g.connect(ctx.destination);
    o.start(start);
    o.stop(start + 0.66);
  }

  /**
   * Play epic victory audio sequence:
   * - Triumphant fanfare
   * - Engine rev celebration
   * - Crowd cheering
   * - Radio voice: "Target has escaped"
   */
  playVictoryAudio(): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    void ctx.resume();
    this.musicImpactDuck = Math.min(1, this.musicImpactDuck + 0.94);
    if (this.bgmEl) {
      const el = this.bgmEl;
      const prevVol = el.volume;
      el.volume = prevVol * 0.11;
      window.setTimeout(() => {
        if (this.bgmEl === el) el.volume = prevVol;
      }, 3600);
    }
    const t = ctx.currentTime;

    // 1. Triumphant fanfare - ascending chord progression
    this.playVictoryFanfare(t);
    
    // 2. Engine rev celebration (delayed)
    this.playVictoryRev(t + 0.3);
    
    // 3. Crowd cheering (delayed)
    this.playVictoryCrowd(t + 0.8);
    
    // 4. Radio voice (delayed)
    this.playVictoryRadio(t + 1.5);
  }
  
  private playVictoryFanfare(start: number): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    const out = this.master;

    // Triumphant ascending notes: C5 -> E5 -> G5 -> C6
    const notes = [523.25, 659.25, 783.99, 1046.5];
    const durations = [0.15, 0.15, 0.15, 0.6];

    let offset = 0;
    for (let i = 0; i < notes.length; i++) {
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.value = notes[i]!;

      const gain = ctx.createGain();
      const noteStart = start + offset;
      gain.gain.setValueAtTime(0.0001, noteStart);
      gain.gain.exponentialRampToValueAtTime(0.52, noteStart + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.32, noteStart + durations[i]! * 0.5);
      gain.gain.exponentialRampToValueAtTime(0.0001, noteStart + durations[i]!);

      osc.connect(gain);
      gain.connect(out);
      osc.start(noteStart);
      osc.stop(noteStart + durations[i]! + 0.1);

      offset += durations[i]! * 0.8;
    }

    const chordNotes = [523.25, 659.25, 783.99];
    for (const note of chordNotes) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = note;

      const gain = ctx.createGain();
      const chordStart = start + 0.5;
      gain.gain.setValueAtTime(0.0001, chordStart);
      gain.gain.exponentialRampToValueAtTime(0.28, chordStart + 0.1);
      gain.gain.exponentialRampToValueAtTime(0.0001, chordStart + 1.5);

      osc.connect(gain);
      gain.connect(out);
      osc.start(chordStart);
      osc.stop(chordStart + 1.6);
    }
  }
  
  private playVictoryRev(start: number): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;

    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(80, start);
    osc.frequency.exponentialRampToValueAtTime(200, start + 0.3);
    osc.frequency.exponentialRampToValueAtTime(350, start + 0.6);
    osc.frequency.exponentialRampToValueAtTime(150, start + 1.0);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.38, start + 0.1);
    gain.gain.setValueAtTime(0.38, start + 0.6);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 1.2);

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(400, start);
    filter.frequency.exponentialRampToValueAtTime(2000, start + 0.5);
    filter.frequency.exponentialRampToValueAtTime(600, start + 1.0);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.master);
    osc.start(start);
    osc.stop(start + 1.3);
  }
  
  private playVictoryCrowd(start: number): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;

    const bufferSize = ctx.sampleRate * 2;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 2000;
    filter.Q.value = 0.5;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.24, start + 0.3);
    gain.gain.setValueAtTime(0.2, start + 1.5);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 2.5);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.master);
    noise.start(start);
    noise.stop(start + 2.6);
  }
  
  private playVictoryRadio(start: number): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    const out = this.master;

    const staticDuration = 0.15;
    const staticBuffer = ctx.createBuffer(1, ctx.sampleRate * staticDuration, ctx.sampleRate);
    const staticData = staticBuffer.getChannelData(0);
    for (let i = 0; i < staticData.length; i++) {
      staticData[i] = (Math.random() * 2 - 1) * 0.3;
    }

    const staticSource = ctx.createBufferSource();
    staticSource.buffer = staticBuffer;

    const staticFilter = ctx.createBiquadFilter();
    staticFilter.type = 'bandpass';
    staticFilter.frequency.value = 3000;
    staticFilter.Q.value = 2;

    const staticGain = ctx.createGain();
    staticGain.gain.value = 0.18;

    staticSource.connect(staticFilter);
    staticFilter.connect(staticGain);
    staticGain.connect(out);
    staticSource.start(start);
    staticSource.stop(start + staticDuration);
    
    // "Target escaped" voice simulation using formant synthesis
    // This creates a robotic/radio voice effect
    const voiceStart = start + 0.2;
    const syllables = [
      { freq: 280, duration: 0.25 }, // "Tar"
      { freq: 220, duration: 0.2 },  // "get"
      { freq: 300, duration: 0.15 }, // "es"
      { freq: 250, duration: 0.25 }, // "caped"
    ];
    
    let offset = 0;
    for (const syl of syllables) {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = syl.freq;
      
      const formant1 = ctx.createBiquadFilter();
      formant1.type = 'bandpass';
      formant1.frequency.value = 700;
      formant1.Q.value = 10;
      
      const formant2 = ctx.createBiquadFilter();
      formant2.type = 'bandpass';
      formant2.frequency.value = 1200;
      formant2.Q.value = 10;
      
      const radioFilter = ctx.createBiquadFilter();
      radioFilter.type = 'bandpass';
      radioFilter.frequency.value = 2000;
      radioFilter.Q.value = 1;
      
      const gain = ctx.createGain();
      const sylStart = voiceStart + offset;
      gain.gain.setValueAtTime(0.0001, sylStart);
      gain.gain.exponentialRampToValueAtTime(0.16, sylStart + 0.02);
      gain.gain.setValueAtTime(0.14, sylStart + syl.duration * 0.7);
      gain.gain.exponentialRampToValueAtTime(0.0001, sylStart + syl.duration);

      osc.connect(formant1);
      formant1.connect(formant2);
      formant2.connect(radioFilter);
      radioFilter.connect(gain);
      gain.connect(out);
      
      osc.start(sylStart);
      osc.stop(sylStart + syl.duration + 0.05);
      
      offset += syl.duration + 0.05;
    }
    
    // End radio static
    const endStatic = ctx.createBufferSource();
    endStatic.buffer = staticBuffer;
    const endStaticGain = ctx.createGain();
    endStaticGain.gain.value = 0.08;
    endStatic.connect(staticFilter);
    staticFilter.connect(endStaticGain);
    endStaticGain.connect(out);
    endStatic.start(voiceStart + offset + 0.1);
    endStatic.stop(voiceStart + offset + 0.25);
  }

  /**
   * High-adrenaline beat sequencer for intense chase moments.
   * Call this from updateMusic when chase intensity is high.
   */
  updateChaseBeat(dt: number, intensity: number): void {
    if (!this.ctx || !this.started || !this.musicBus) return;

    this.chaseIntensity = intensity;
    this.beatBpm = 130 + intensity * 50;
    const stepDuration = 60 / this.beatBpm / 4;

    this.beatTimer += dt;
    if (this.beatTimer >= stepDuration) {
      this.beatTimer -= stepDuration;
      this.playBeatStep();
      this.beatStep++;
    }
  }

  private playBeatStep(): void {
    if (!this.ctx || !this.musicBus) return;
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const beat = this.beatStep % 16;
    const bar = Math.floor(this.beatStep / 16) % 4;
    const intensity = this.chaseIntensity;

    if (beat === 0 || beat === 4 || beat === 8 || beat === 12) {
      this.playKick(now, intensity);
    }

    if (beat === 2 || beat === 6 || beat === 10 || beat === 14) {
      this.playSnare(now, intensity);
    }

    if (beat % 2 === 0) {
      this.playHiHat(now, false, intensity);
    } else if (intensity > 0.5) {
      this.playHiHat(now, true, intensity);
    }

    const bassNotes = [55, 55, 73.42, 65.41];
    if (beat === 0 || beat === 8) {
      this.playBass(now, bassNotes[bar]!, 0.25, intensity);
    }
    if (intensity > 0.6 && (beat === 4 || beat === 12)) {
      this.playBass(now, bassNotes[bar]! * 1.5, 0.12, intensity);
    }

    if (intensity > 0.7 && beat === 0 && bar === 0) {
      this.playSynthArp(now, [329.63, 392, 440, 392], intensity);
    }

    if (intensity > 0.8 && beat === 0 && bar === 3) {
      this.playRiser(now, true, intensity);
    }
  }

  private playKick(start: number, intensity: number): void {
    if (!this.ctx || !this.musicBus) return;
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(150 + intensity * 30, start);
    osc.frequency.exponentialRampToValueAtTime(40, start + 0.1);

    const vol = 0.15 + intensity * 0.1;
    gain.gain.setValueAtTime(vol, start);
    gain.gain.exponentialRampToValueAtTime(0.001, start + 0.18);

    osc.connect(gain);
    gain.connect(this.musicBus);

    osc.start(start);
    osc.stop(start + 0.2);
  }

  private playSnare(start: number, intensity: number): void {
    if (!this.ctx || !this.musicBus) return;
    const ctx = this.ctx;

    const bufferSize = Math.floor(ctx.sampleRate * 0.12);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 1200 + intensity * 800;

    const gain = ctx.createGain();
    const vol = 0.08 + intensity * 0.06;
    gain.gain.setValueAtTime(vol, start);
    gain.gain.exponentialRampToValueAtTime(0.001, start + 0.1);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.musicBus);

    noise.start(start);
    noise.stop(start + 0.12);
  }

  private playHiHat(start: number, open: boolean, intensity: number): void {
    if (!this.ctx || !this.musicBus) return;
    const ctx = this.ctx;

    const duration = open ? 0.12 : 0.04;
    const bufferSize = Math.floor(ctx.sampleRate * duration);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 8000;

    const gain = ctx.createGain();
    const vol = 0.03 + intensity * 0.02;
    gain.gain.setValueAtTime(vol, start);
    gain.gain.exponentialRampToValueAtTime(0.001, start + duration);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.musicBus);

    noise.start(start);
    noise.stop(start + duration);
  }

  private playBass(start: number, freq: number, duration: number, intensity: number): void {
    if (!this.ctx || !this.musicBus) return;
    const ctx = this.ctx;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc.type = 'sawtooth';
    osc.frequency.value = freq;

    filter.type = 'lowpass';
    filter.frequency.value = 400 + intensity * 800;

    const vol = 0.08 + intensity * 0.06;
    gain.gain.setValueAtTime(vol, start);
    gain.gain.exponentialRampToValueAtTime(0.001, start + duration);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.musicBus);

    osc.start(start);
    osc.stop(start + duration);
  }

  private playSynthArp(start: number, notes: number[], intensity: number): void {
    if (!this.ctx || !this.musicBus) return;
    const ctx = this.ctx;
    const musicBus = this.musicBus;
    const noteDur = 0.1;

    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();

      osc.type = 'square';
      osc.frequency.value = freq;

      filter.type = 'lowpass';
      filter.frequency.value = 2000 + intensity * 3000;

      const noteStart = start + i * noteDur;
      const vol = 0.03 + intensity * 0.02;
      gain.gain.setValueAtTime(0, noteStart);
      gain.gain.linearRampToValueAtTime(vol, noteStart + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, noteStart + noteDur);

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(musicBus);

      osc.start(noteStart);
      osc.stop(noteStart + noteDur);
    });
  }

  private playRiser(start: number, drop: boolean, intensity: number): void {
    if (!this.ctx || !this.musicBus) return;
    const ctx = this.ctx;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc.type = 'sawtooth';

    if (drop) {
      osc.frequency.setValueAtTime(880, start);
      osc.frequency.exponentialRampToValueAtTime(55, start + 0.25);
      const vol = 0.06 + intensity * 0.04;
      gain.gain.setValueAtTime(vol, start);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.25);
    } else {
      osc.frequency.setValueAtTime(220, start);
      osc.frequency.exponentialRampToValueAtTime(880, start + 0.4);
      const vol = 0.02 + intensity * 0.03;
      gain.gain.setValueAtTime(vol, start);
      gain.gain.linearRampToValueAtTime(vol * 2, start + 0.35);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.4);
    }

    filter.type = 'lowpass';
    filter.frequency.value = 3000;

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(this.musicBus);

    osc.start(start);
    osc.stop(start + 0.45);
  }

  /** Sparse procedural “radio” bursts — no external assets; feels like dispatcher chatter. */
  private radioChatterTimer = 0;
  private radioClipBuffers: AudioBuffer[] = [];
  private radioClipsLoadStarted = false;

  private async loadOptionalRadioClips(ctx: AudioContext): Promise<void> {
    if (RADIO_CHATTER_CLIP_URLS.length === 0) return;
    if (this.radioClipsLoadStarted) return;
    this.radioClipsLoadStarted = true;
    const out: AudioBuffer[] = [];
    for (const url of RADIO_CHATTER_CLIP_URLS) {
      try {
        const res = await fetch(url);
        if (!res.ok) continue;
        const ab = await res.arrayBuffer();
        const buf = await ctx.decodeAudioData(ab.slice(0));
        out.push(buf);
      } catch {
        /* missing or undecodable file */
      }
    }
    this.radioClipBuffers = out;
  }

  tickRadioChatter(
    dt: number,
    opts: {
      phase: 'playing' | 'won' | 'lost';
      bustMeter: number;
      wantedStars: number;
    },
  ): void {
    if (!this.ctx || !this.started || !this.master) return;
    if (opts.phase !== 'playing') return;
    if (opts.bustMeter < 0.2 && opts.wantedStars < 2) return;
    this.radioChatterTimer -= dt;
    if (this.radioChatterTimer <= 0) {
      this.radioChatterTimer = 14 + Math.random() * 20;
      this.playRadioChatterBurst();
    }
  }

  private playRadioChatterBurst(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;
    const clips = this.radioClipBuffers;
    const useClip = clips.length > 0 && Math.random() < 0.52;

    if (useClip) {
      this.playMegaphoneStinger(ctx, t, 0.26);
      const buf = clips[Math.floor(Math.random() * clips.length)]!;
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const g = ctx.createGain();
      const dur = Math.min(buf.duration, 2.8);
      const t0 = t + 0.05;
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.2, t0 + 0.04);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 120;
      const shelf = ctx.createBiquadFilter();
      shelf.type = 'peaking';
      shelf.frequency.value = 2400;
      shelf.gain.value = 4;
      shelf.Q.value = 0.9;
      src.connect(hp);
      hp.connect(shelf);
      shelf.connect(g);
      g.connect(this.master);
      src.start(t0);
      src.stop(t0 + dur + 0.02);
      return;
    }

    this.playMegaphoneStinger(ctx, t, 1);
    this.playProceduralRadioBeeps(ctx, t + 0.055);
  }

  /** Bullhorn / PA punch — distinct from the high “radio” bleeps. */
  private playMegaphoneStinger(
    ctx: AudioContext,
    t: number,
    gainScale: number,
  ): void {
    if (!this.master) return;
    const freqs = [325, 405];
    for (let k = 0; k < freqs.length; k++) {
      const o = ctx.createOscillator();
      o.type = 'square';
      o.frequency.setValueAtTime(freqs[k]!, t + k * 0.038);
      const g = ctx.createGain();
      const v = 0.048 * gainScale;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(v, t + 0.018 + k * 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16 + k * 0.04);
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass';
      f.frequency.value = 880;
      f.Q.value = 0.85;
      const pan =
        typeof ctx.createStereoPanner === 'function'
          ? ctx.createStereoPanner()
          : null;
      o.connect(f);
      f.connect(g);
      if (pan) {
        pan.pan.value = k === 0 ? -0.35 : 0.35;
        g.connect(pan);
        pan.connect(this.master);
      } else {
        g.connect(this.master);
      }
      o.start(t);
      o.stop(t + 0.22);
    }

    const n = Math.floor(ctx.sampleRate * 0.045);
    const cone = ctx.createBuffer(1, n, ctx.sampleRate);
    const cd = cone.getChannelData(0);
    for (let i = 0; i < n; i++) {
      cd[i] = (Math.random() * 2 - 1) * (1 - i / n) * 0.65;
    }
    const ns = ctx.createBufferSource();
    ns.buffer = cone;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.014 * gainScale, t);
    ng.gain.exponentialRampToValueAtTime(0.0001, t + 0.045);
    const bpf = ctx.createBiquadFilter();
    bpf.type = 'bandpass';
    bpf.frequency.value = 1400;
    bpf.Q.value = 1.4;
    ns.connect(bpf);
    bpf.connect(ng);
    ng.connect(this.master);
    ns.start(t);
    ns.stop(t + 0.05);
  }

  /** Synthetic dispatcher radio — narrowband bleeps + static burst. */
  private playProceduralRadioBeeps(ctx: AudioContext, t: number): void {
    if (!this.master) return;
    const phrases: number[][] = [
      [780, 660, 900],
      [720, 850, 680],
      [880, 740, 820],
      [800, 800, 760],
    ];
    const mel = phrases[Math.floor(Math.random() * phrases.length)]!;
    const pan =
      typeof ctx.createStereoPanner === 'function'
        ? ctx.createStereoPanner()
        : null;
    if (pan) pan.pan.value = (Math.random() - 0.5) * 0.5;

    for (let i = 0; i < mel.length; i++) {
      const o = ctx.createOscillator();
      o.type = 'square';
      o.frequency.setValueAtTime(mel[i]!, t + i * 0.068);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t + i * 0.068);
      g.gain.exponentialRampToValueAtTime(0.035, t + i * 0.068 + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, t + i * 0.068 + 0.1);
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass';
      f.frequency.value = 1600 + Math.random() * 400;
      f.Q.value = 2.4;
      o.connect(f);
      f.connect(g);
      if (pan) {
        g.connect(pan);
      } else {
        g.connect(this.master);
      }
      o.start(t + i * 0.068);
      o.stop(t + i * 0.068 + 0.11);
    }
    if (pan) pan.connect(this.master);

    const n = Math.floor(ctx.sampleRate * 0.07);
    const noiseBuf = ctx.createBuffer(1, n, ctx.sampleRate);
    const nd = noiseBuf.getChannelData(0);
    for (let i = 0; i < n; i++) {
      nd[i] = (Math.random() * 2 - 1) * (1 - i / n) * 0.9;
    }
    const ns = ctx.createBufferSource();
    ns.buffer = noiseBuf;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.02, t);
    ng.gain.exponentialRampToValueAtTime(0.0001, t + 0.07);
    const bpf = ctx.createBiquadFilter();
    bpf.type = 'bandpass';
    bpf.frequency.value = 2600;
    bpf.Q.value = 1.8;
    ns.connect(bpf);
    bpf.connect(ng);
    ng.connect(this.master);
    ns.start(t);
    ns.stop(t + 0.075);
  }
}
